import type { ModuleDto } from '@app/contracts';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { assignModule, fetchAllModules, fetchSiteModules, unassignModule } from '../api';

export const KEY_MODULES_ALL = ['modules', 'all'] as const;
export const keySiteModules = (siteId: string) => ['modules', 'site', siteId] as const;

export const useAllModules = () =>
  useQuery({ queryKey: KEY_MODULES_ALL, queryFn: fetchAllModules, staleTime: 60_000 });

export const useSiteModules = (siteId: string | undefined) =>
  useQuery({
    queryKey: keySiteModules(siteId ?? ''),
    queryFn: () => {
      if (!siteId) throw new Error('siteId requis');
      return fetchSiteModules(siteId);
    },
    enabled: !!siteId,
    staleTime: 10_000,
  });

/** Modules ISO assignés à au moins un des sites (union, dédupliquée par id). */
export function useIsoModulesForSiteIds(siteIds: string[]): {
  modules: ModuleDto[];
  isLoading: boolean;
} {
  const queries = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: keySiteModules(siteId),
      queryFn: () => fetchSiteModules(siteId),
      staleTime: 10_000,
    })),
  });

  const byId = new Map<string, ModuleDto>();
  for (const q of queries) {
    if (q.data) {
      for (const m of q.data) {
        if (m.active) byId.set(m.id, m);
      }
    }
  }
  const modules = [...byId.values()].sort((a, b) => a.code.localeCompare(b.code, 'fr'));
  const isLoading = siteIds.length > 0 && queries.length > 0 && queries.some((q) => q.isLoading);

  return { modules, isLoading };
}

export function useAssignModule(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moduleId: string) => assignModule(siteId, moduleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keySiteModules(siteId) }),
  });
}

export function useUnassignModule(siteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moduleId: string) => unassignModule(siteId, moduleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keySiteModules(siteId) }),
  });
}
